import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCreditCard, faUser, faCalendarAlt, faEye, faTimes, faFilter } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { showToast } from '@/utils/toasts';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import MultiSelectDropdown from '@/components/ui/MultiSelectDropdown';
import {
  type Subscription,
  type SubscriptionStatus,
} from '@/models';
import { useSubscriptionProducts } from '@/contexts/SubscriptionProductsContext';

const PAGE_SIZE = 50;

const SUBSCRIPTION_STATUS_OPTIONS: { value: SubscriptionStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Aktiv' },
  { value: 'PAST_DUE', label: 'Überfällig' },
  { value: 'CANCELED', label: 'Gekündigt' },
  { value: 'ABORTED', label: 'Checkout abgebrochen' },
  { value: 'PENDING_PAYMENT', label: 'Zahlung ausstehend' },
  { value: 'INCOMPLETE', label: 'Unvollständig' },
  { value: 'FAILED', label: 'Fehlgeschlagen' }
];

export default function AdminSubscriptionsList() {
  const { getTypeLabel } = useSubscriptionProducts();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<SubscriptionStatus[]>(['ACTIVE', 'PAST_DUE']);
  const [cancelingSubscriptionId, setCancelingSubscriptionId] = useState<string | null>(null);

  useEffect(() => {
    fetchSubscriptions();
  }, [currentPage, statusFilter]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;
      const response = await APIRoutes.listAllSubscriptions({
        limit: PAGE_SIZE,
        next: cursorToken,
        status: statusFilter.length > 0 ? statusFilter : undefined
      });

      setSubscriptions(response.subscriptions);
      setTotal(response.total);

    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      showToast('Fehler beim Laden der Subscriptions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async (subscription: Subscription) => {
    if (!confirm(`Möchten Sie wirklich die Subscription für ${subscription.user?.email ?? 'diesen Nutzer'} kündigen?`)) {
      return;
    }

    setCancelingSubscriptionId(subscription.id);
    try {
      await APIRoutes.cancelSubscription(subscription.userID, subscription.id);
      showToast('Subscription erfolgreich gekündigt', 'success');
      fetchSubscriptions();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      showToast('Fehler beim Kündigen der Subscription', 'error');
    } finally {
      setCancelingSubscriptionId(null);
    }
  };

  const getStatusColor = (status: SubscriptionStatus) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400';
      case 'PAST_DUE':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400';
      case 'CANCELED':
      case 'ABORTED':
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400';
      case 'PENDING_PAYMENT':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-400';
      case 'INCOMPLETE':
      case 'FAILED':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-400';
    }
  };

  const getStatusLabel = (status: SubscriptionStatus) => {
    switch (status) {
      case 'ACTIVE':
        return 'Aktiv';
      case 'PAST_DUE':
        return 'Überfällig';
      case 'CANCELED':
        return 'Gekündigt';
      case 'ABORTED':
        return 'Checkout abgebrochen';
      case 'PENDING_PAYMENT':
        return 'Zahlung ausstehend';
      case 'INCOMPLETE':
        return 'Unvollständig';
      case 'FAILED':
        return 'Fehlgeschlagen';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    if (dateString === null || dateString === undefined || dateString === '') return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const formatPrice = (cents: number) => {
    return `${(cents / 100).toFixed(2)} €`;
  };

  const handleStatusFilterChange = (selected: SubscriptionStatus[]) => {
    setStatusFilter(selected);
    setCurrentPage(1);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <FontAwesomeIcon icon={faCreditCard} className="w-6 h-6 mr-3" />
          Subscriptions Verwaltung
        </h1>
      </div>

      {/* Filter Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <MultiSelectDropdown
            options={SUBSCRIPTION_STATUS_OPTIONS}
            selected={statusFilter}
            onChange={handleStatusFilterChange}
            label="Status Filter"
            icon={<FontAwesomeIcon icon={faFilter} className="w-4 h-4 mr-2" />}
            placeholder="Alle Status"
            dropdownClassName="status-filter-dropdown"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {statusFilter.length > 0 ? 'Gefiltert' : 'Gesamt'}: <span className="font-semibold text-gray-900 dark:text-white">{total}</span> Subscriptions
        </div>
      </div>

      {/* Subscriptions Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
      ) : subscriptions.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
          <p className="text-gray-500 dark:text-gray-400">
            {statusFilter.length === 0 ? 'Keine Subscriptions gefunden' : 'Keine Subscriptions mit den ausgewählten Status gefunden'}
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Benutzer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Produkte
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Preis
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Erstellt
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Nächste Zahlung
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Empfehlung durch
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {subscriptions.map((subscription) => {
                    return (
                      <tr key={subscription.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link
                            to={`/admin/users/${subscription.userID}`}
                            className="flex items-center text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            <FontAwesomeIcon icon={faUser} className="w-4 h-4 mr-2" />
                            <div>
                              <div className="text-sm font-medium">{subscription.user.firstName} {subscription.user.lastName}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{subscription.user.email ?? ''}</div>
                            </div>
                          </Link>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            {subscription.items.map(item => (
                              <div className="flex flex-row gap-2">
                                <span className="text-sm text-gray-900 dark:text-white">{item.type}</span>
                                <span className="text-sm text-gray-500 dark:text-gray-400">{ getTypeLabel(item.type) }</span>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(subscription.status)}`}>
                            {getStatusLabel(subscription.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {formatPrice(subscription.price)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <FontAwesomeIcon icon={faCalendarAlt} className="w-3 h-3 mr-1" />
                          {formatDate(subscription.created)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {subscription.currentPeriodEnd !== null && subscription.currentPeriodEnd !== undefined && subscription.currentPeriodEnd !== '' ? (
                            <>
                              <FontAwesomeIcon icon={faCalendarAlt} className="w-3 h-3 mr-1" />
                              {formatDate(subscription.currentPeriodEnd)}
                            </>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {subscription.referrer !== null && subscription.referrerUserID !== null ? (
                            <Link
                              to={`/admin/users/${subscription.referrerUserID}`}
                              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              {subscription.referrer.firstName} {subscription.referrer.lastName}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <Link
                              to={`/admin/users/${subscription.userID}`}
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer"
                              title="Details anzeigen"
                            >
                              <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
                            </Link>
                            {subscription.status === 'ACTIVE' && (
                              <button
                                onClick={() => handleCancelSubscription(subscription)}
                                disabled={cancelingSubscriptionId === subscription.id}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50 cursor-pointer"
                                title="Subscription kündigen"
                              >
                                {cancelingSubscriptionId === subscription.id ? (
                                  <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                                ) : (
                                  <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="mt-6">
            <PageBasedPagination
              currentPage={currentPage}
              totalCount={total}
              pageSize={PAGE_SIZE}
              onPageChange={setCurrentPage}
              loading={loading}
            />
          </div>
        </>
      )}
    </div>
  );
}
